/**
 * messageTemplateリポジトリの実装
 * DMと配信で使用するメッセージテンプレートのFirestore操作を管理
 * @date 2025/06/25
 */
import React from 'react';
import {
  getDocs,
  collection,
  Firestore,
  query,
  QueryConstraint,
  where,
  limit,
  addDoc,
  getDoc,
  doc,
  orderBy,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore/lite';
import { useFirestoreClient } from 'src/firebase';
import { MessageTemplate, getFieldName, toMessageTemplateId } from 'src/models/messageTemplate';
import { Collection } from 'src/models/common';
import { toMessageTemplateFromDoc } from './converter';
import { MessageTemplateRepository } from './interface';
import { LoginAccountState } from 'src/globalStates/loginAccount';
import { toCreateFsDoc, toUpdateFsDoc } from '../common/firestore';
import {
  MessageTemplateGetItemQuery,
  MessageTemplateGetListQuery,
} from 'src/usecases/messageTemplate/reader';

/**
 * messageTemplateリポジトリを使用するためのカスタムフック
 * DBクライアントをメモ化して返却
 */
export const useMessageTemplateRepository = (): MessageTemplateRepository => {
  const db = useFirestoreClient();

  return React.useMemo(() => createMessageTemplateRepository(db), [db]);
};

/**
 * messageTemplateリポジトリのインスタンスを作成
 * @param db Firestoreクライアント
 */
const createMessageTemplateRepository = (db: Firestore) => {
  return {
    /**
     * テンプレート一覧を取得
     * アクティブなテンプレートを作成日時の降順で取得
     */
    async getList({ account }: MessageTemplateGetListQuery) {
      if (account.merchantId === undefined) {
        throw new Error('マーチャントIDが読み取れません');
      }

      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageTemplates
        }`
      );

      const queries: Array<QueryConstraint> = [
        where(getFieldName('documentStatus'), '==', 'active'),
        orderBy('createdAt', 'desc'),
      ];

      const snapshot = await getDocs(query(collRef, ...queries, limit(200)));
      return snapshot.docs.map((doc) => 
        toMessageTemplateFromDoc(doc.data(), doc.id)
      );
    },

    /**
     * 個別のテンプレートを取得
     * 指定されたIDのテンプレートを取得
     */
    async getDoc({ account, messageTemplateId: templateId }: MessageTemplateGetItemQuery) {
      if (account.merchantId === undefined) {
        throw new Error('マーチャントIDが読み取れません');
      }

      const docRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageTemplates
        }/${templateId.toString()}`
      );

      const snapshot = await getDoc(docRef);
      if (snapshot.data() === undefined) {
        throw new Error('テンプレートの取得に失敗しました');
      }

      return toMessageTemplateFromDoc(snapshot.data()!, snapshot.id);
    },

    /**
     * 新規テンプレートを作成
     * 作成後、作成したテンプレートを返却
     */
    async createDoc(messageTemplate: MessageTemplate, account: LoginAccountState) {
      if (account.merchantId === undefined) {
        throw new Error('マーチャントIDが読み取れません');
      }

      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageTemplates
        }`
      );

      const _result = await addDoc(collRef, toCreateFsDoc(messageTemplate));
      return this.getDoc({ messageTemplateId: toMessageTemplateId(_result.id), account });
    },

    /**
     * テンプレートを更新
     * 更新後、更新したテンプレートを返却
     */
    async updateDoc(messageTemplate: MessageTemplate, account: LoginAccountState) {
      if (account.merchantId === undefined) {
        throw new Error('マーチャントIDが読み取れません');
      }

      const docRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageTemplates
        }/${messageTemplate.id}`
      );

      await updateDoc(docRef, toUpdateFsDoc(messageTemplate));
    },

    /**
     * テンプレートを論理削除
     * 削除後、何も返却しない
     */
    async deleteDoc(messageTemplate: MessageTemplate, account: LoginAccountState) {
      if (account.merchantId === undefined) {
        throw new Error('マーチャントIDが読み取れません');
      }
      const docRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageTemplates
        }/${messageTemplate.id}`
      );
      await updateDoc(docRef, {
        deletedAt: serverTimestamp(),
        documentStatus: 'deleted',
      });
    },
  };
};